// node_modules
import { FC, useCallback, useEffect, useState } from "react";
// Components
import { DeleteButton, FindestEditableSpan } from "Components";
// Types
import { TRequirementDTO } from "Types";
// Custom Hooks
import { useDebounce } from "Hooks";
// Enums
import { ToastTypeEnum } from "Enums";
// Helpers
import { ToastHelperSingleton } from "Helpers";
// Controllers
import { RequirementControllerSingleton } from "Controllers";
import { CharacterButton } from "Components/Shared";

export type TCaseRequirementProps = {
    caseId: number,
    requirement: TRequirementDTO,
    requirementDeletedHandler: (requirementDeletedId: number) => void,
    requirementUpdatedHandler: (requirementUpdated: TRequirementDTO) => void
};

export const CaseRequirement: FC<TCaseRequirementProps> = ({caseId, requirement, requirementDeletedHandler, requirementUpdatedHandler}: TCaseRequirementProps) => {
    // State
    const [currentRequirement, setCurrentRequirement] = useState<TRequirementDTO | undefined>(undefined);
    const [requirementName, setRequirementName] = useState<string | undefined>(undefined);

    // Const
    const debouncedRequirementName = useDebounce(requirementName, 500);

    // Logic
    const updateRequirementAsync = useCallback(async(newRequirementName: string, newRequirementType: number): Promise<void> => {
        // safety-checks
        if (!currentRequirement || newRequirementName === null || newRequirementName === undefined || 
            newRequirementType < 1 || newRequirementType > 2) { return; }

        // call server to update requirement
        const isSuccess: boolean = await RequirementControllerSingleton
            .updateRequirementAsync(caseId, currentRequirement.id, newRequirementName, newRequirementType);

        // tell user about update result 
        if (!isSuccess) {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Was not able to update requirement.");
        } else {
            ToastHelperSingleton.showToast(ToastTypeEnum.Success, "Successfully updated requirement.");
        }

        requirementUpdatedHandler(currentRequirement);
        
        return;
    }, [caseId, currentRequirement, requirementUpdatedHandler]);

    const deleteRequirementAsync = async(): Promise<void> => {
        // call server to delete requirement
        const isSuccess: boolean = await RequirementControllerSingleton
            .deleteRequirementAsync(requirement.id);

        // tell user about delete result 
        if (!isSuccess) {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Was not able to delete requirement.");
        } else {
            ToastHelperSingleton.showToast(ToastTypeEnum.Success, "Successfully deleted requirement.");
        }

        // call parent deletion handler
        requirementDeletedHandler(requirement.id);
    };

    const updateRequirementTypeAsync = async(newRequirementType: number): Promise<void> => {
        if (!currentRequirement) { return; }

        currentRequirement.type = newRequirementType;
        setCurrentRequirement(currentRequirement);

        // send requirement update to the server
        updateRequirementAsync(currentRequirement.name, newRequirementType);
    };

    useEffect(() => {
        setRequirementName(undefined);
        if (requirement) {
            setCurrentRequirement(requirement);
            return;
        }
        setCurrentRequirement(undefined);
    }, [requirement]);

    useEffect(() => {
        // do not trigger edit at first render, when switching to a new requirement
        // or when displaying a new requirement
        if (!currentRequirement || debouncedRequirementName === null || debouncedRequirementName === undefined) { return; }

        currentRequirement.name = debouncedRequirementName;
        setCurrentRequirement(currentRequirement);

        // send requirement update to the server
        updateRequirementAsync(debouncedRequirementName, currentRequirement.type);
    }, [currentRequirement, debouncedRequirementName, updateRequirementAsync]);
    
    // Render
    return (
        <>
            {currentRequirement ?
                    <div>
                        <FindestEditableSpan
                            entityId={requirement.id}
                            text={currentRequirement.name}
                            onChangeText={(text: string) => setRequirementName(text)} />
                        <CharacterButton
                            character="C"
                            isActive={currentRequirement.type === 1}
                            onClick={() => updateRequirementTypeAsync(1)} />
                        <CharacterButton
                            character="O"
                            isActive={currentRequirement.type === 2}
                            onClick={() => updateRequirementTypeAsync(2)} />
                        <DeleteButton
                            onClick={deleteRequirementAsync}/>
                    </div>
                :
                    null
            }
        </>
    );
};
